import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { autoSignIn, signUp } from 'aws-amplify/auth';
import { readNext } from '../../auth/postAuthRedirect.ts';
import { getRecaptchaToken, isRecaptchaError, preloadRecaptcha } from '../../auth/recaptcha.ts';
import { reportRecaptchaFailure } from '../../auth/recaptchaReport.ts';
import { recordSignupFailure } from '../../telemetry/rum.ts';
import AuthModeToggle from '../../components/auth/AuthModeToggle.tsx';
import AuthShell from '../../components/auth/AuthShell.tsx';
import ConsentCheckbox from '../../components/auth/ConsentCheckbox.tsx';
import EmailInput from '../../components/auth/EmailInput.tsx';
import PasswordInput from '../../components/auth/PasswordInput.tsx';
import TextInput from '../../components/auth/TextInput.tsx';
import UsernameInput from '../../components/auth/UsernameInput.tsx';
import { buildConsentPayload } from '../../auth/useConsent.ts';
import { humanizeAuthError } from '../../auth/errors.ts';
import { isFlaggedRoute, routeForNextStep } from '../../auth/signInNextStep.ts';
import './SignUp.css';

interface Consents {
  tos: boolean;
  privacy: boolean;
  ai: boolean;
}

export default function SignUp() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [givenName, setGivenName] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [consents, setConsents] = useState<Consents>({
    tos: false,
    privacy: false,
    ai: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when the last failure was a client-side reCAPTCHA mint failure, so we
  // offer an explicit "Try again" (the mint self-heals its script cache).
  const [retryable, setRetryable] = useState(false);
  // Validity reported by UsernameInput (all 4 criteria, including the
  // async uniqueness check). Gates the submit button.
  const [usernameValid, setUsernameValid] = useState(false);
  const [emailValid, setEmailValid] = useState(false);

  // Warm the reCAPTCHA script on mount so minting is instant at submit. The
  // post-signup auto-sign-in goes through the same DefineAuthChallenge gate as
  // manual sign-in, which fails closed without a token (see recaptcha.ts).
  useEffect(() => {
    preloadRecaptcha();
  }, []);

  const allConsented = consents.tos && consents.privacy && consents.ai;
  const passwordsMatch = password.length > 0 && password === confirmPassword;
  const filled =
    email.trim().length > 0 &&
    username.trim().length > 0 &&
    password.length > 0 &&
    confirmPassword.length > 0 &&
    givenName.trim().length > 0 &&
    familyName.trim().length > 0;
  const canSubmit =
    filled && allConsented && emailValid && usernameValid && passwordsMatch && !submitting;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    void attemptSignUp();
  }

  // Extracted so the "Try again" button (shown after a reCAPTCHA mint
  // failure) can re-run the flow without a synthetic form event. A mint
  // failure throws before signUp() is called, so a retry never creates a
  // partial account — a fresh Cognito username is generated each attempt.
  async function attemptSignUp() {
    setError(null);
    setRetryable(false);
    setSubmitting(true);
    const startedAt = Date.now();
    try {
      // Pool has `signInAliases: { username, email }`, which means the
      // `username` field on signUp cannot itself be email-format. Generate
      // a random Cognito-internal username and store the user's chosen
      // handle as `preferred_username` (their display identity).
      const cognitoUsername = `om-${crypto.randomUUID()}`;
      // The auto-sign-in below replays as a CUSTOM_WITH_SRP signIn, which the
      // server-side DefineAuthChallenge gate rejects without a reCAPTCHA token
      // (fails closed — same gate manual sign-in satisfies). Amplify forwards
      // the `autoSignIn` object as the replayed signIn's `options`, so the
      // token rides through on its `clientMetadata`. v3 tokens are single-use
      // and ~2 min, and signUp→autoSignIn is immediate, so minting here is fine.
      const recaptchaToken = await getRecaptchaToken('login');
      // PreSignUp auto-confirms the user + auto-verifies their email,
      // so signUp returns `isSignUpComplete: true` and we can chain
      // straight into autoSignIn — no `/auth/verify` step. autoSignIn
      // uses the password just submitted to drive a CUSTOM_WITH_SRP
      // signIn under the hood; on a brand-new account the device is
      // unknown, so the magic-link challenge fires and we route to
      // /auth/check-email.
      await signUp({
        username: cognitoUsername,
        password,
        options: {
          autoSignIn: {
            authFlowType: 'CUSTOM_WITH_SRP',
            clientMetadata: { recaptchaToken },
          },
          userAttributes: {
            email,
            given_name: givenName,
            family_name: familyName,
            preferred_username: username,
            'custom:consents': JSON.stringify(buildConsentPayload()),
          },
        },
      });
      const signInOut = await autoSignIn();
      const next = routeForNextStep(signInOut);
      if (isFlaggedRoute(next)) {
        // Bot-flagged on the auto sign-in after signup (task #28): the
        // account exists; send them to sign-in, where a re-flag shows the
        // full guidance panel.
        navigate('/auth/signin', { state: { email } });
        return;
      }
      if (!next) {
        setError(
          `Unhandled sign-in step: ${signInOut.nextStep.signInStep}. Please sign in manually.`,
        );
        navigate('/auth/signin', { state: { email } });
        return;
      }
      // A new account is always an unknown device, so `next` here is the
      // magic-link check-email step. Forward any `?next=` target (set by a
      // marketing CTA, e.g. /agent/uno) into its state so the user lands
      // there once approval completes instead of the generic /app chooser.
      const requestedNext = readNext(location.search);
      if (next.path === '/auth/check-email') {
        navigate(next.path, {
          state: { ...next.state, next: requestedNext ?? undefined },
          replace: true,
        });
      } else {
        navigate(requestedNext ?? next.path, { state: next.state, replace: true });
      }
    } catch (err) {
      // A client-side reCAPTCHA mint failure never reached the server, so
      // report it (fire-and-forget) for server-side visibility, and offer a
      // retry — the mint self-heals its script cache, so a re-attempt can
      // succeed after a transient stall.
      if (isRecaptchaError(err)) {
        reportRecaptchaFailure(err, 'signup', Date.now() - startedAt);
        setRetryable(true);
      }
      // Handled failures never hit RUM's uncaught-error telemetry — record
      // the funnel's last step explicitly (reason only, never the identity).
      recordSignupFailure(
        isRecaptchaError(err) ? `recaptcha:${err.reason}` : (err instanceof Error ? err.name : 'unknown'),
        err instanceof Error ? err.message : undefined,
      );
      setError(humanizeAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Create account"
      subtitle="We'll email you a one-tap approval link to finish setting up. New devices use the same link; remembered devices skip it."
      topNav={<AuthModeToggle current="signup" />}
    >
      <form className="om-signup" onSubmit={handleSubmit} noValidate>
        <EmailInput
          label="Email"
          value={email}
          onChange={setEmail}
          required
          disabled={submitting}
          onValidityChange={setEmailValid}
        />
        <UsernameInput
          label="Username"
          value={username}
          onChange={setUsername}
          required
          disabled={submitting}
          onValidityChange={setUsernameValid}
        />
        <div className="om-signup__row">
          <TextInput
            label="First name"
            value={givenName}
            onChange={setGivenName}
            autoComplete="given-name"
            required
            disabled={submitting}
          />
          <TextInput
            label="Last name"
            value={familyName}
            onChange={setFamilyName}
            autoComplete="family-name"
            required
            disabled={submitting}
          />
        </div>
        <PasswordInput
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          required
          minLength={12}
          disabled={submitting}
          showCriteria
        />
        <PasswordInput
          label="Confirm password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          required
          minLength={12}
          disabled={submitting}
          matchAgainst={password}
        />

        <fieldset className="om-signup__consents" disabled={submitting}>
          <legend className="om-signup__consents-legend">Required agreements</legend>
          <ConsentCheckbox
            label="Terms of Service"
            docPath="/legal/terms"
            checked={consents.tos}
            onChange={(v) => setConsents((c) => ({ ...c, tos: v }))}
          />
          <ConsentCheckbox
            label="Privacy Policy"
            docPath="/legal/privacy"
            checked={consents.privacy}
            onChange={(v) => setConsents((c) => ({ ...c, privacy: v }))}
          />
          <ConsentCheckbox
            label="AI Consent Notice"
            docPath="/legal/ai-consent"
            checked={consents.ai}
            onChange={(v) => setConsents((c) => ({ ...c, ai: v }))}
          />
        </fieldset>

        {error ? (
          <p className="om-signup__error" role="alert">
            {error}
          </p>
        ) : null}
        {retryable ? (
          <button
            type="button"
            className="om-signup__retry"
            onClick={() => void attemptSignUp()}
            disabled={submitting}
          >
            {submitting ? 'Retrying…' : 'Try again'}
          </button>
        ) : null}

        <button
          type="submit"
          className="om-signup__submit"
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </button>
      </form>
    </AuthShell>
  );
}
